import { useDownload } from '@modules/file-menagement';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Doc } from 'src/@types/docs';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useDocsUIState } from 'src/state/ui/useDocsUIState';
import { useDocsActionBarUIState } from '../../../../../../features/ActionsBar/Docs/useDocsActionBarUIState';
import { useDocDuplicateMutation } from '../../../shared/useDocDuplicateMutation';
import { useDocExportMutation } from '../../../shared/useDocExportMutation';
import { useDocFavoriteMutation } from '../../../shared/useDocFavoritedMutation';
import { useDocMutation } from '../../../shared/useDocMutation';
import { useUpdateDocMutation } from '../../../shared/useUpdateDocMutation';

export const useDocHeaderActions = ({ doc }: { doc: Doc }) => {
  const navigate = useNavigate();

  const { createDoc, deleteDoc, leaveDoc } = useDocMutation();
  const { docsFullWidth, setDocsFullWidth } = useDocsUIState(doc._id);
  const { duplicateDoc, isDuplicatingDoc } = useDocDuplicateMutation();
  const { exportDoc, isExportingDoc } = useDocExportMutation();
  const { updateDoc, isUpdateDocLoading } = useUpdateDocMutation();
  const { downloadContent, loadingMap } = useDownload();

  const { docsIDList, addDocToList, deleteDocFromList } =
    useDocsActionBarUIState();

  const { setFavorite, isFavoritingDoc, setDisfavor, isDisfavoringDoc } =
    useDocFavoriteMutation();

  const handleFavorite = async () => {
    const isLoading = isFavoritingDoc || isDisfavoringDoc;
    if (isLoading) return;
    if (doc.starred) {
      await setDisfavor(doc._id);
      return;
    }
    await setFavorite(doc._id);
  };

  const goToDoc = (docReturn: Doc) => {
    navigate(PATH_DASHBOARD.docs.view(docReturn._id));
  };

  const handleCreateDoc = async () => {
    const docReturn = await createDoc({ parent: doc._id });

    if (docReturn) {
      goToDoc(docReturn);
    }
  };

  const handleSwitchChange = async () => {
    setDocsFullWidth(!docsFullWidth);
  };

  const handleDuplicate = async () => {
    if (doc && !isDuplicatingDoc) {
      await duplicateDoc(doc._id);
    }
  };

  const handleToogleShowFooter = async () => {
    if (isUpdateDocLoading) return;
    await updateDoc({
      _id: doc._id,
      showFooter: !doc.showFooter,
    });
  };

  const handleExport = async () => {
    if (doc && !isExportingDoc) {
      const blob = await exportDoc(doc._id);
      const file = new File([blob], `${doc.title}.dcp`);
      downloadContent({ fileName: `${doc.title}.dcp`, blob: file });
    }
  };

  const handleDeleteDoc = async () => {
    await deleteDoc(doc._id);
    navigate(PATH_DASHBOARD.docs.root);
  };

  const handleLeaveDoc = async () => {
    await leaveDoc(doc._id);
    navigate(PATH_DASHBOARD.docs.root);
  };

  const isOnActionBar = useMemo(
    () => docsIDList.includes(doc._id),
    [docsIDList, doc],
  );

  const toggleDocOnActionBar = () => {
    if (isOnActionBar) {
      deleteDocFromList(doc._id);
      return;
    }
    addDocToList(doc._id);
  };

  return {
    handleCreateDoc,
    handleToogleShowFooter,
    handleSwitchChange,
    handleDuplicate,
    handleDeleteDoc,
    handleLeaveDoc,
    handleExport,
    handleFavorite,
    toggleDocOnActionBar,
    isOnActionBar,
  };
};
